// CompanyContext.js - Company selection state management
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { useAuth } from './AuthContext';
import { setActiveCompanyId } from '../utils/activeCompany';

const CompanyContext = createContext(null);

export const useCompany = () => {
  const context = useContext(CompanyContext);
  if (!context) {
    throw new Error('useCompany must be used within a CompanyProvider');
  }
  return context;
};

// Special value for "All Companies" view
export const ALL_COMPANIES = 'all';

export const CompanyProvider = ({ children }) => {
  const { userProfile, isAuthenticated } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState(() => {
    // Restore last-selected company from localStorage to avoid flash
    try {
      const stored = localStorage.getItem('selectedCompanyId');
      if (stored) return stored;
    } catch { /* ignore */ }
    return ALL_COMPANIES;
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Last explicit in-app company switch. Until the URL catches up with it,
  // this wins over the `?company=` param (see the URL-sync effect below).
  const pendingSelectionRef = useRef(null);
  const COMPANIES_CACHE_KEY = 'sb-companies-cache';

  // Load cached companies instantly (same pattern as profile cache)
  const loadCachedCompanies = () => {
    try {
      const raw = localStorage.getItem(COMPANIES_CACHE_KEY);
      if (raw) {
        const { data, ts } = JSON.parse(raw);
        if (data && Date.now() - ts < 24 * 60 * 60 * 1000) {
          return data;
        }
      }
    } catch (e) { /* corrupted cache */ }
    return null;
  };

  // Fetch companies the user has access to
  const fetchCompanies = useCallback(async () => {
    if (!isAuthenticated) {
      setCompanies([]);
      setLoading(false);
      return;
    }

    // Wait for userProfile to load (need at least the user ID)
    if (!userProfile?.id) {
      console.log('CompanyContext: No user profile yet, skipping company fetch');
      setCompanies([]);
      setLoading(false);
      return;
    }

    // Show cached companies instantly (no spinner)
    const cached = loadCachedCompanies();
    if (cached) {
      setCompanies(cached);
      setLoading(false);
    }

    try {
      setLoading(!cached); // Only show spinner if no cache

      // Use profile from AuthContext (already in memory, no extra network call).
      // The previous code re-fetched user_profiles here, duplicating Query 2
      // and adding ~1-3s to every login. AuthContext's profile is always fresh
      // at mount time (just fetched from Supabase). If admin changes company
      // assignments, the user re-logs in and gets the latest data.
      const profile = userProfile;
      console.log('CompanyContext: Using profile for role:', profile.role,
        '| default:', profile.default_company_id,
        '| managed:', profile.managed_company_ids,
        '| explicit:', profile.company_ids);

      const isSuperAdminUser = profile.role === 'super_admin';

      // Build effective company IDs from all possible sources
      // Handles JSONB arrays, native arrays, null, and edge cases
      let managedIds = [];
      if (Array.isArray(profile.managed_company_ids)) {
        managedIds = profile.managed_company_ids;
      } else if (profile.managed_company_ids && typeof profile.managed_company_ids === 'object') {
        // Edge case: JSONB returned as object instead of array
        managedIds = Object.values(profile.managed_company_ids);
      }
      // Filter out any null/undefined IDs that might have slipped in
      managedIds = managedIds.filter(id => id != null);

      const explicitIds = Array.isArray(profile.company_ids) ? profile.company_ids : [];
      const defaultId = profile.default_company_id;

      const effectiveCompanyIds = [...new Set([
        ...(defaultId ? [defaultId] : []),
        ...managedIds,
        ...explicitIds,
      ])];

      console.log('CompanyContext: effectiveCompanyIds =', effectiveCompanyIds);

      // Fetch companies (handle case where table doesn't exist yet)
      let query = supabase
        .from('companies')
        .select('*')
        .eq('is_active', true)
        .order('name');

      // If not super admin, filter by user's effective company IDs
      if (!isSuperAdminUser && effectiveCompanyIds.length > 0) {
        query = query.in('id', effectiveCompanyIds);
      }

      const { data, error: fetchError } = await query;

      console.log('CompanyContext: Query result — error:', fetchError?.message || 'none', '| data count:', data?.length ?? 'null/undefined');

      if (fetchError) {
        console.warn('Could not fetch companies (table may not exist yet):', fetchError.message);
        setCompanies([]);
        setLoading(false);
        return;
      }

      let finalData = data || [];

      console.log('CompanyContext: Companies loaded:', finalData.length);
      setCompanies(finalData);
      // Cache for instant load on next refresh
      try {
        localStorage.setItem(COMPANIES_CACHE_KEY, JSON.stringify({ data: finalData, ts: Date.now() }));
      } catch (e) { /* localStorage full */ }

      // Check localStorage first for previously selected company
      const savedCompanyId = localStorage.getItem('selectedCompanyId');
      const companyIds = data?.map(c => c.id) || [];

      // Restore the All-Companies view if the user had it selected and may
      // still see it (same visibility rule as canViewAllCompanies)
      const canRestoreAll = data?.length > 0 &&
        (isSuperAdminUser || profile.role === 'admin' || data.length > 1);

      // If user had a company selected and it's still valid, restore it
      if (savedCompanyId === ALL_COMPANIES && canRestoreAll) {
        console.log('Restoring All-Companies view');
        setSelectedCompanyId(ALL_COMPANIES);
      } else if (savedCompanyId && savedCompanyId !== ALL_COMPANIES && companyIds.includes(savedCompanyId)) {
        console.log('Restoring saved company:', savedCompanyId);
        setSelectedCompanyId(savedCompanyId);
      } else if (defaultId && companyIds.includes(defaultId)) {
        // Use user's default company if available
        setSelectedCompanyId(defaultId);
      } else {
        // Set default company based on available companies
        const hasMultipleCompanies = data?.length > 1;
        const canViewAll = isSuperAdminUser || profile.role === 'admin' || profile.role === 'manager' || profile.role === 'finance' || hasMultipleCompanies;
        
        if (canViewAll && !defaultId && data?.length > 1) {
          // Default to "All Companies" for multi-company users without a specific default
          setSelectedCompanyId(ALL_COMPANIES);
        } else if (data?.length === 1) {
          // Only one company → auto-select it
          setSelectedCompanyId(data[0].id);
        } else if (data?.length > 0) {
          // Multiple companies → select first one
          setSelectedCompanyId(data[0].id);
        }
      }
    } catch (err) {
      console.error('Error fetching companies:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, userProfile]);

  // Fetch companies when auth state changes
  useEffect(() => {
    if (isAuthenticated && userProfile) {
      fetchCompanies();
    }
  }, [isAuthenticated, userProfile, fetchCompanies]);

  // ── URL sync: ?company=<id> for multi-company users ──────────────────
  // Multi-company user + concrete company → URL always carries ?company=<id>
  // (survives refresh, bookmarks, and navigation between menu sections).
  // "All Companies", single-company users, and employees → no param.
  const applyCompanyParam = useCallback((companyValue) => {
    const params = new URLSearchParams(location.search);
    const current = params.get('company');
    if (companyValue == null) {
      if (!params.has('company')) return; // already absent
      params.delete('company');
    } else {
      if (current === companyValue) return; // already correct
      params.set('company', companyValue);
    }
    const search = params.toString();
    // Deliberately does NOT carry location.state: this fires as a same-URL
    // replace while the user is mid-navigation, and replaying stale state
    // (e.g. SubmitClaimWizard's successMessage, Dashboard's noAccess toast)
    // would re-trigger one-shot UI.
    navigate(
      { pathname: location.pathname, search: search ? `?${search}` : '' },
      { replace: true }
    );
  }, [navigate, location.pathname, location.search]);

  // Keep URL and selection in sync. Runs on every route change (so the param
  // follows the user across menu sections), on company switches, and after
  // the companies list loads. Handles back/forward and pasted links: a valid
  // param different from the current selection is ADOPTED; an invalid one is
  // stripped. Single-company users/employees never carry a param.
  useEffect(() => {
    if (loading || !isAuthenticated) return; // wait until the list is known

    const params = new URLSearchParams(location.search);
    const urlCompany = params.get('company');
    const multiCompany = companies.length > 1;

    // Single-company users and employees: never show the param
    if (!multiCompany) {
      if (params.has('company')) applyCompanyParam(null);
      return;
    }

    // An explicit in-app switch always wins over a stale URL param. The URL
    // update from switchCompany can land in a later commit than the selection
    // state (React Router defers navigation updates), so without this guard
    // the adopt branch below would grab the stale param and revert the switch
    // (e.g. "All Companies" snapping back to the previously selected company).
    const pending = pendingSelectionRef.current;
    if (pending != null) {
      const pendingIsAll = pending === ALL_COMPANIES;
      const urlMatchesPending = pendingIsAll ? !urlCompany : urlCompany === pending;
      if (urlMatchesPending) {
        pendingSelectionRef.current = null; // URL caught up with the switch
      } else {
        applyCompanyParam(pendingIsAll ? null : pending);
        return;
      }
    }

    // Multi-company: URL param present and different from the selection
    if (urlCompany && urlCompany !== selectedCompanyId) {
      if (companies.some((c) => c.id === urlCompany)) {
        // Adopt from back/forward, bookmark, or pasted link
        setSelectedCompanyId(urlCompany);
        localStorage.setItem('selectedCompanyId', urlCompany);
        return;
      }
      // Invalid or not-permitted id — falls through to repair below
      // (RLS still governs the actual data access)
    }

    // In sync, or repair after paste/clean navigation/invalid param.
    // "All Companies" → param removed; concrete company → param set.
    applyCompanyParam(selectedCompanyId === ALL_COMPANIES ? null : selectedCompanyId);
  }, [location.pathname, location.search, loading, isAuthenticated, companies, selectedCompanyId, applyCompanyParam]);

  // Switch to a different company
  const switchCompany = (companyId) => {
    // Remember the explicit choice until the URL param catches up with it, so
    // the URL-sync effect's adopt branch cannot revert this switch.
    pendingSelectionRef.current = companyId;
    setSelectedCompanyId(companyId);

    // Store in localStorage for persistence across sessions (including the
    // All-Companies view, so a refresh lands back where the user was).
    // Cleared on fresh sign-in (AuthContext SIGNED_IN) and on logout.
    if (companyId !== ALL_COMPANIES) {
      localStorage.setItem('selectedCompanyId', companyId);
    } else {
      localStorage.setItem('selectedCompanyId', ALL_COMPANIES);
    }

    // Reflect the choice in the URL immediately (batched with the state
    // update, so the sync effect sees a consistent URL + selection).
    if (companies.length > 1) {
      applyCompanyParam(companyId === ALL_COMPANIES ? null : companyId);
    }
  };

  // Get the currently selected company object
  const getSelectedCompany = () => {
    if (selectedCompanyId === ALL_COMPANIES) return null;
    return companies.find(c => c.id === selectedCompanyId) || null;
  };

  // Check if viewing all companies
  const isAllCompaniesView = () => selectedCompanyId === ALL_COMPANIES;

  // Get company name by ID
  const getCompanyName = (companyId) => {
    if (!companyId) return 'Unknown Company';
    const company = companies.find(c => c.id === companyId);
    return company?.name || 'Unknown Company';
  };

  // Get company short code by ID
  const getCompanyShortCode = (companyId) => {
    if (!companyId) return '';
    const company = companies.find(c => c.id === companyId);
    return company?.short_code || '';
  };

  // Check if user can access a specific company
  const canAccessCompany = (companyId) => {
    if (userProfile?.role === 'super_admin') return true;
    const managedIds = Array.isArray(userProfile?.managed_company_ids) ? userProfile.managed_company_ids : [];
    const explicitIds = Array.isArray(userProfile?.company_ids) ? userProfile.company_ids : [];
    return explicitIds.includes(companyId)
      || managedIds.includes(companyId)
      || userProfile?.default_company_id === companyId;
  };

  // Check if user can view "All Companies" option
  const canViewAllCompanies = () => {
    // Super admin and admin can always see "All Companies" if any companies exist
    if (userProfile?.role === 'super_admin' || userProfile?.role === 'admin') {
      return companies.length > 0;
    }
    // Manager and finance can see "All Companies" only if they actually have multiple companies
    if ((userProfile?.role === 'manager' || userProfile?.role === 'finance') && companies.length > 1) {
      return true;
    }
    return false;
  };

  // Refresh companies list
  const refreshCompanies = () => {
    fetchCompanies();
  };

  // Mirror the selection for AuthContext's company-aware permission checks
  // (per-company overrides in user_profiles.company_permissions). Synchronous
  // during render — children read it before effects run; "All Companies" →
  // null so base permissions apply with no company scope.
  setActiveCompanyId(selectedCompanyId === ALL_COMPANIES ? null : selectedCompanyId);

  const value = {
    companies,
    selectedCompanyId,
    loading,
    error,
    switchCompany,
    getSelectedCompany,
    isAllCompaniesView,
    getCompanyName,
    getCompanyShortCode,
    canAccessCompany,
    canViewAllCompanies,
    refreshCompanies,
    ALL_COMPANIES,
  };

  return (
    <CompanyContext.Provider value={value}>
      {children}
    </CompanyContext.Provider>
  );
};

export default CompanyContext;
